import styled from '@emotion/styled';
import { useState } from 'react';
import Image from '@base/FutureImage';
import Gallery from 'aquilla/core/molecules/Gallery';
import { segment } from '@actions/analytics';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import { useRouter } from 'next/router';
import useWindowSize from '@hooks/useWindowSize';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import Typography from 'aquilla/core/atoms/Typography';
import Carousel from 'aquilla/core/molecules/Carousel';
import { Spacings, mediaQuery, Fonts } from '../../../styleConstants';
import { CENTRE_IMAGE_VIEWED } from './constants';

const StyledGallery = styled(Gallery)`
  width: 1024px;
  height: 600px;
  ${mediaQuery.uptoMobile} {
    width: 100vw;
  }
`;

const Container = styled.div`
  grid-column: span 24;
  width: var(--layout-width);
  text-align: center;
  margin-bottom: ${Spacings.SPACING_22B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: auto;
    margin-top: ${Spacings.SPACING_2B};
    flex-direction: column;
    padding-bottom: ${Spacings.SPACING_8B};
    border-bottom: solid ${Spacings.SPACING_2B} var(--color-violet-1);
  }
`;

const CarouselStyImage = styled(Image)`
  border-radius: ${Spacings.SPACING_2B};
  object-fit: cover;
  cursor: pointer;
  margin-left: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_0B};
    border-radius: ${Spacings.SPACING_2B};
  }
`;

const CarouselWrapper = styled.div`
  grid-column: span 24;
  width: 100%;
  height: auto;
  display: flex;
  & .aquilla-carousel-wrapper {
    justify-content: center;
    ${mediaQuery.uptoMobile} {
      margin-left: ${Spacings.SPACING_4B};
      margin-right: ${Spacings.SPACING_4B};
    }
  }
  & .aquilla-carousel-wrapper div {
    ${mediaQuery.uptoMobile} {
      :last-child {
        margin-right: ${Spacings.SPACING_4B};
      }
      gap: ${Spacings.SPACING_2B};
    }
  }
`;

const ImageWrapper = styled(Image)`
  width: 1024px;
  height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  ${mediaQuery.uptoMobile} {
    width: 100vw;
    height: auto;
  }
`;

const StyledHeading = styled.span`
  color: var(--color-base-fill);
`;

const ColoredHeading = styled(Typography)`
  color: var(--color-i-violet-15);
  font-size: ${Fonts.H2};
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
    font-weight: ${Fonts.HEAVY};
  }
`;

const StyledEducSubHeading = styled(Typography)`
  max-width: 650px;
  margin: auto;
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_12B};
  color: var(--color-text-secondary);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H5};
    font-weight: ${Fonts.LIGHT};
    line-height: ${Spacings.SPACING_6B};
  }
`;

const ImagesList = ({ data }) => {
  const { heroImage, heroImage2, images, heading, subHeading } = data;

  const carouselImages = [heroImage, heroImage2, ...images];
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);
  const { setImagesClicked } = useOfflineCentreStore();
  const [close, setClose] = useState(true);
  const { isMobile } = useWindowSize();
  const { centreName, cityName } = centreInfo || {};
  const [slideNumber, setSlideNumber] = useState(1);

  const onClickImage = (imageIndex) => {
    setClose(false);
    if (typeof window !== 'undefined' && window.document) {
      document.body.style.overflow = 'hidden';
    }
    setImagesClicked(true);
    segment.track(CENTRE_IMAGE_VIEWED, {
      centre_name: centreName,
      city: cityName,
      goal_uid: routeGoalUID
    });
    setSlideNumber(imageIndex + 1);
  };
  const imageList = [heroImage, heroImage2, ...images];

  const closeGallery = () => {
    setImagesClicked(false);
    setClose(true);
    document.body.style.overflow = 'unset';
  };

  const renderHeading = () => {
    if (isMobile) return null;
    return (
      <>
        {heading ? (
          <ColoredHeading variant="h4">
            <StyledHeading>{heading}</StyledHeading>
          </ColoredHeading>
        ) : (
          <ColoredHeading variant="h4">
            State-of-the-art <StyledHeading>infrastructure</StyledHeading>
          </ColoredHeading>
        )}
        <StyledEducSubHeading variant="p1">
          {subHeading ||
            'Learning in classrooms is nothing new, Then what it is which makes Unacademy special? we get it! and that’s why we have completely reimagined learning experience for you'}
        </StyledEducSubHeading>
      </>
    );
  };
  if (!carouselImages?.length > 0) {
    return null;
  }
  return (
    <Container>
      {renderHeading()}
      <StyledGallery
        open={!close}
        onClose={closeGallery}
        startAtSlideNumber={slideNumber}
      >
        {imageList.map((img) => {
          return (
            <ImageWrapper
              src={img}
              alt="images-gallery"
              layout="fill"
              key={img}
            />
          );
        })}
      </StyledGallery>
      <CarouselWrapper>
        <Carousel
          itemsPerSlide={5}
          variant="multiple"
          mode="auto"
          controls={{ showArrows: true }}
        >
          {carouselImages.map((imgSrc, i) => (
            <CarouselStyImage
              src={imgSrc}
              width={isMobile ? 104 : 320}
              height={isMobile ? 70 : 200}
              key={`carousel-${imgSrc}`}
              alt={`img-${i}`}
              onClick={() => onClickImage(i)}
            />
          ))}
        </Carousel>
      </CarouselWrapper>
    </Container>
  );
};

export default ImagesList;
